/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { format, parseISO } from 'date-fns';
import { Mail, Pencil, Phone } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { type Therapist } from '../../lib/api/resources.js';
import { formatInr } from '../../lib/utils/money.js';

import { Stars, therapistInitials } from './therapist-card.js';
import { TherapistDocumentsPanel } from './therapist-documents-panel.js';
import { AdminTherapistTimesheetPanel } from './therapist-timesheet-panel.js';

function ProfileStat({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800">
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-white">{value}</p>
    </div>
  );
}

/** Pay rates (CR-003): per-session for all; monthly base only for on-roll staff. */
function RatesGrid({ therapist }: { readonly therapist: Therapist }): JSX.Element {
  const { t } = useTranslation();
  const perSession =
    therapist.perSessionRatePaise !== null ? formatInr(therapist.perSessionRatePaise) : '—';
  const monthly =
    therapist.category === 'on_roll' && therapist.monthlyFixedPaise !== null
      ? formatInr(therapist.monthlyFixedPaise)
      : '—';
  return (
    <div className="grid grid-cols-2 gap-3">
      <ProfileStat label={t('form.therapist.perSessionRate')} value={perSession} />
      <ProfileStat label={t('form.therapist.monthlyFixed')} value={monthly} />
    </div>
  );
}

/** Therapist Profile modal body (Figma) — opened from a therapist card. */
export function TherapistProfile({
  therapist,
  onEdit,
}: {
  readonly therapist: Therapist;
  readonly onEdit?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const joined =
    therapist.joinedOn !== null ? format(parseISO(therapist.joinedOn), 'MMM yyyy') : '—';
  const specialisations =
    therapist.specialisations.length > 0 ? therapist.specialisations.join(', ') : '—';

  return (
    <div className="space-y-4">
      <div className="bg-[var(--accent)]/10 flex items-center gap-4 rounded-2xl p-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent)] text-lg font-bold text-white">
          {therapistInitials(therapist.fullName)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-lg font-semibold text-slate-800 dark:text-white">
            {therapist.fullName}
          </p>
          <p className="truncate text-sm text-slate-500 dark:text-slate-400">{specialisations}</p>
          <div className="mt-1 flex items-center gap-2">
            <Badge
              label={t(`form.therapist.categoryOption.${therapist.category}`)}
              tone={therapist.category === 'consultant' ? 'amber' : 'violet'}
            />
            {therapist.rating !== null && (
              <span className="flex items-center gap-1.5">
                <Stars rating={therapist.rating} />
                <span className="text-sm text-slate-500">({therapist.rating.toFixed(1)})</span>
              </span>
            )}
          </div>
        </div>
        {onEdit !== undefined && (
          <Button type="button" variant="outline" onClick={onEdit}>
            <Pencil className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {t('common.edit')}
          </Button>
        )}
      </div>

      <RatesGrid therapist={therapist} />

      <div className="grid grid-cols-2 gap-3">
        <ProfileStat
          label={t('admin.therapistProfile.activeClients')}
          value={String(therapist.clientsCount)}
        />
        <ProfileStat
          label={t('admin.therapistProfile.totalSessions')}
          value={String(therapist.sessionsCount)}
        />
        <ProfileStat label={t('admin.therapistProfile.joinDate')} value={joined} />
        <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('admin.therapistProfile.status')}
          </p>
          <div className="mt-1">
            <Badge
              label={t(`status.${therapist.status}`)}
              tone={therapist.status === 'active' ? 'green' : 'slate'}
            />
          </div>
        </div>
      </div>

      <div className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800">
        <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Mail className="h-4 w-4 text-slate-400" aria-hidden="true" />
          {therapist.email}
        </p>
        {therapist.phone !== null && (
          <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <Phone className="h-4 w-4 text-slate-400" aria-hidden="true" />
            {therapist.phone}
          </p>
        )}
      </div>

      <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
        <AdminTherapistTimesheetPanel therapistId={therapist.id} />
      </div>

      <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
        <TherapistDocumentsPanel therapistId={therapist.id} />
      </div>
    </div>
  );
}
